import Ajv, { JSONSchemaType, ValidateFunction, DefinedError } from 'ajv';
import AjvErrors from 'ajv-errors';

// Tool types
import type { Errors, Nullable } from './types';

// Export for easy further usage
export { Errors, DefinedError };

/**
 *  Validator for validating input based on JSON schemas.
 */
export default class Validator<Schema, Subschema = void> {

    /**
     *  Holding the validator
     */
    private _validator!: ValidateFunction<Schema>;

    /**
     *
     *  Used to create a new validator for the provided schema.
     *
     *  @param schema The schema to use for validation
     *  @param subschemas Subschema that are used in the (main) schema
     *
     */
    constructor(schema: JSONSchemaType<Schema>, subschemas?: Array<JSONSchemaType<Subschema>>) {

        /**
         *
         *  Create a new AJV instance with errors for compiling
         *  schemas and creating validation functions.
         *
         *  (i) With errors so we can directly use the error
         *  messages from the provided schemas
         *
         */
        const ajv = AjvErrors(new Ajv({
            allErrors: true,
            schemas: subschemas,
        }));

        this._validator = ajv.compile<Schema>(schema);

    }

    /**
     *
     *  Used to construct the errors object for the model to validate.
     *
     *  (i) Taking the object structure into account
     *
     *  @param data The data object to construct an errors object for
     *
     *  @returns The constructed errors object
     *
     */
    public static constructErrors<Schema>(data: Nullable<Schema>): Errors<Schema> {

        const errors = {} as Errors<Schema>;

        /**
         *
         *  Used to recursively construct the error object.
         *
         *  @param data The data object to construct the errors for
         *  @param key The key in the data to construct the errors for
         *  @param errors The errors object to add the error field to
         *
         */
        const addErrorField = (data: Nullable<Schema>, key: keyof Schema, errors: Errors<Schema>) => {

            /**
             *  Check if the value is an object.
             */
            const value = data[key] as string | null | Record<string, unknown>;

            if (value === null || value.constructor.name !== 'Object') {

                errors[key] = null;

                return;

            }

            /**
             *  Add a lower level object in case of an object and start adding his fields.
             */
            errors[key] = errors[key] || {} as Errors<Schema>;

            for (const subkey of Object.keys(value) as Array<keyof Schema>) {

                addErrorField(data[key] as unknown as Nullable<Schema>, subkey, errors[key] as Errors<Schema>);

            }

        };

        /**
         *  Start to add the error properties recursively.
         */
        for (const key of Object.keys(data) as Array<keyof Schema>) {

            addErrorField(data, key, errors);

        }

        return errors;

    }

    /**
     *
     *  Used to validate the forwarded data.
     *
     *  @param data The data object to validate
     *  @param errors The constructed errors object (ref), used for directly handling and updating errors
     *
     *  @returns If the providevariabled data is valid
     *
    */
    public validate(data: Nullable<Schema>, errors: Errors<Schema>): boolean {

        const valid = this._validator(data);

        this._handleValidation(errors, this._validator.errors as Array<DefinedError> | undefined);

        return valid;

    }

    /**
     *
     *  Used to handle the validation: cleaning the error's object
     *  and filling it with validation error messages.
     *
     *  @param errors The errors object to update
     *  @param validationErrors The errors of the validation
     *
     *  @private
     *
     */
    private _handleValidation<Schema>(errors: Errors<Schema>, validationErrors?: Array<DefinedError>): void {

        /**
         *
         *  Used to recursively update the data errors
         *
         *  @param errors The errors object to update the errors for
         *  @param key The key of the data to update his error for
         *
         */
        const checkErrorField = (errors: Errors<Schema>, key: keyof Schema) => {

            /**
             *  Check if the value is an object and start recursion if so.
             */
            const error = errors[key] as string | null | Record<string, unknown>;

            if (error !== null && error.constructor.name === 'Object') {

                for (const subkey of Object.keys(error) as Array<keyof Schema>) {

                    checkErrorField(error as Errors<Schema>, subkey);

                }

                return;

            }

            /**
             *  Always reset the error first, so the error is empty
             *  if no error was thrown for this field.
             */
            errors[key] = null;

            /**
             *  Check if an error got thrown for the
             *  field and update it if so.
             */
            for (const validationError of validationErrors || []) {

                /**
                 *  The exact property can always be found
                 *  as last part of the instance path.
                 */
                const validationErrorPath: Array<string> = validationError.instancePath.split('/');
                const validationErrorKey = validationErrorPath[validationErrorPath.length - 1];

                if (validationErrorKey !== key) {

                    continue;

                }

                /**
                 *  If an empty string is provided as message, it be set
                 *  as true so the input only shows an orange border.
                 */
                errors[key] = validationError.message && validationError.message.length ? validationError.message : true;

                break;

            }

        };

        /**
         *  Start to update the errors recursively.
         */
        for (const key of Object.keys(errors) as Array<keyof Schema>) {

            checkErrorField(errors, key);

        }

    }

}
